import React, { useContext, useEffect, useState } from 'react';
import ARSTabs from './ArsTabs';
import { HexagonIcon, RulesIcon, SubModelIcon } from 'assests/GmsIcons';
import ARSPageHeader from './ArsPageHeader';
import { getModelData, updateRecommenderModel } from '../ARSServices/ARSServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import PageSkeleton from 'Components/DesignSystem/PageSkeleton/PageSkeleton';
import ArsDefineModel from './ArsDefineModel';
import PromotionModal from './PromotionModal';
import { ArsContext } from '../Contexts/ArsContext';
import { useNavigate } from "react-router";
import { RecommenderLogsResponse, TCombineModesConfig, TSetModePriority } from '../Types';
import { validateMinProductCount, validateModeTitles } from '../ModeTransformUtils';

type ArsModelComponentProps = {
    match: {
        params: {
            modelId: string;
        };
    };
};

const ArsModelComponent = ({ match }: ArsModelComponentProps) => {
    const { params } = match;
    const { modelId } = params;
    const [loading, setLoading] = useState(false);
    const [showPromote, setShowPromote] = useState<boolean>(false);
    const {
        industryId,
        model,
        setModel,
        activeSubmodelIndex,
        setActiveSubmodelIndex,
        fetchCategories,
        fetchCategoryTypes
    } = useContext(ArsContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (modelId) {
            getModel();
            if (activeSubmodelIndex !== 0) {
                setActiveSubmodelIndex(0);
            }
        }
    }, [modelId]);

    // Category/category-type dropdowns further down this tree (BaseCategorySelector,
    // TargetCategorySelector, DefaultSubmodelBaseSelector, ArsDefineModel) all read this
    // cache from ArsContext. Fetch (and re-fetch on industry switch, same as before) only
    // while this ARS page is actually mounted, instead of ArsContext auto-fetching on every
    // industry switch app-wide regardless of whether the user is anywhere near ARS.
    useEffect(() => {
        if (industryId) {
            fetchCategoryTypes();
            fetchCategories();
        }
    }, [industryId]);

    const getModel = async () => {
        try {
            setLoading(true);
            const res = await getModelData(modelId);
            if (res?.done) {
                setModel(res?.data);
            }
        } catch (error) {
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const tabLabels = [
        { label: 'Summary', icon: <SubModelIcon />, disable: true },
        { label: 'Model', icon: <SubModelIcon />, disable: false },
        { label: 'Rules', icon: <RulesIcon />, disable: true },
        { label: 'AB Testing', icon: <HexagonIcon />, disable: true },
        { label: 'Analytics', icon: <SubModelIcon />, disable: true },
        { label: 'UI Builder', icon: <HexagonIcon />, disable: true }
    ];

    const [activeTab, setActiveTab] = useState<number>(1);

    const handleTabClick = (index: number) => setActiveTab(index);

    const updateSetModePriority = (
        setModePriority: TSetModePriority,
        submodelId: string
    ) => {
        setModel((prev: RecommenderLogsResponse | null) => {
            if (!prev) return prev;

            const newObj = { ...prev };

            if (newObj?.submodel && newObj.submodel.length > 0) {
                const updatedSubmodels = [...newObj.submodel];
                const submodelIndex = updatedSubmodels.findIndex(
                    (submodel) => submodel.submodel_id === submodelId
                );
                if (submodelIndex !== -1) {
                    updatedSubmodels[submodelIndex] = {
                        ...updatedSubmodels[submodelIndex],
                        setModePriority
                    };
                }
                newObj.submodel = updatedSubmodels;
            }
            return newObj;
        });
    };

    const updateCombineModesConfig = (
        config: TCombineModesConfig,
        submodelId: string
    ) => {
        setModel((prev: RecommenderLogsResponse | null) => {
            if (!prev) return prev;

            const newObj = { ...prev };

            if (newObj?.submodel && newObj.submodel.length > 0) {
                const updatedSubmodels = [...newObj.submodel];
                const submodelIndex = updatedSubmodels.findIndex(
                    (submodel) => submodel.submodel_id === submodelId
                );
                if (submodelIndex !== -1) {
                    updatedSubmodels[submodelIndex] = {
                        ...updatedSubmodels[submodelIndex],
                        combine_modes_config: config
                    };
                }
                newObj.submodel = updatedSubmodels;
            }
            return newObj;
        });
    };

    const togglePromoteModal = () => {
        setShowPromote((prev) => !prev);
    };

    const promotionHandler = () => {
        // Implementation for promotion
    };

    const validateModel = (): { isValid: boolean; message?: string } => {
        if (!model?.submodel) {
            return { isValid: false, message: 'No submodels found' };
        }

        for (const submodel of model.submodel) {
            const { setModePriority, combine_modes_config = { enabled: false } } = submodel;

            if (!validateModeTitles(setModePriority, combine_modes_config)) {
                return {
                    isValid: false,
                    message: 'All mode titles are required. Please ensure each mode has a valid title.'
                };
            }

            if (!validateMinProductCount(setModePriority, combine_modes_config)) {
                return {
                    isValid: false,
                    message: 'The minimum number of products must be greater than zero.'
                };
            }
        }

        return { isValid: true };
    };

    const updateModel = async (): Promise<void> => {
        if (!industryId) return;

        try {
            const validation = validateModel();

            if (!validation.isValid) {
                /*
                  Narrowed once, up front (§ 116). `validateModel` sets a message on
                  every invalid return, so the fallback is unreachable today — but the
                  return type says `message?`, and react-toastify accepted `undefined`
                  by rendering an empty toast. The DS toast takes a `string`, so tsc
                  surfaced what was previously a silent empty-notification path.
                */
                const message = validation.message ?? 'Model is not valid.';
                if (message.includes('title')) {
                    toast.error(message);
                } else {
                    toast.warning(message);
                }
                return;
            }

            const res = await updateRecommenderModel(model as RecommenderLogsResponse);

            if (res?.done) {
                toast.success('Model updated successfully');
                navigate('/gms/commend', { replace: true });
            } else {
                toast.error(res?.message ?? 'Failed to update model');
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to update model';
            toast.error(errorMessage);
        }
    };

    return loading ? (
        <PageSkeleton preset="detail" label="Loading model" data-testid="ars-model-loading" />
    ) : (
        <React.Fragment>
            <ARSPageHeader
                model={model as RecommenderLogsResponse}
                onSave={updateModel}
                togglePromoteModal={togglePromoteModal}
            />
            <div className="tw-flex tw-justify-center tw-border-b tw-border-t tw-border-solid tw-border-lightgray-200">
                <ARSTabs
                    tabLabels={tabLabels}
                    activeTab={activeTab}
                    onTabClick={handleTabClick}
                />
            </div>

            {activeTab === 1 ? (
                <ArsDefineModel
                    model={model}
                    setModel={setModel}
                    updateSetModePriority={updateSetModePriority}
                    updateCombineModesConfig={updateCombineModesConfig}
                />
            ) : null}

            {activeTab === 2 ? (
                <label className="tw-text-black tw-text-[24px] tw-font-semibold">
                    Coming Soon.....
                </label>
            ) : null}

            <PromotionModal
                showModal={showPromote}
                closeModal={togglePromoteModal}
                onConfirm={promotionHandler}
            />
        </React.Fragment>
    );
};

export default ArsModelComponent;